import * as React from 'react';
import { Box, Modal, Spinner } from '@leagueplatform/genesis-core';
import { EffectiveTime } from '@leagueplatform/dashboard-api';
import { track } from '@leagueplatform/analytics';
import { useChartData } from '#pages/details4/hooks/use-chart-data.ts';
import { ErrorPanel } from '#components/error-panel/index.ts';
import { ParseValueForInput } from '#utils/parse-value-for-input.util.ts';
import { AddEditDataModalContent } from './add-edit-data-modal-content';
import { AddEditDataModalContentContainer } from './add-edit-data-modal-content-container';

type AddEditDataManagedModalProps = {
  onClose: () => void;
  metricType?: string;
  observationId?: string;
  value?: number | string | null;
  label?: string;
  timestamp?: string;
  effectiveDateTime?: EffectiveTime;
  onSuccessSubmit?: () => void;
};

/**
 * A self-contained add-edit data modal which fetches input config details
 * using the metricType.
 */
export function AddEditDataManagedModal({
  onClose,
  metricType,
  observationId,
  value,
  label,
  timestamp,
  effectiveDateTime,
  onSuccessSubmit,
}: AddEditDataManagedModalProps) {
  const chartDataQuery = useChartData(
    {
      metric: metricType!,
      from: new Date(),
    },
    { enabled: !!metricType },
  );

  const chartData = chartDataQuery?.data?.data[0];
  const metricConfigId = chartData?.id;

  React.useEffect(() => {
    if (metricConfigId) {
      track('dashboard.screen_loaded.metric_data_entry_screen_metric_type', {
        metric_type: metricConfigId || '',
        metric_id: '',
        metric_status: '',
      });
    }
  }, [metricConfigId]);

  return (
    <Modal.Root
      open={!!metricType}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AddEditDataModalContentContainer>
        {chartDataQuery.isLoading ? (
          <Box
            css={{
              display: 'flex',
              position: 'absolute',
              inset: 0,
              justifyContent: 'center',
              alignItems: 'center',
              color: '$decorativeBrandPrimaryDefault',
            }}
          >
            <Spinner loading />
          </Box>
        ) : null}
        {chartDataQuery.isError ? (
          <ErrorPanel
            onRetry={chartDataQuery.refetch}
            error={chartDataQuery.error}
            isRefetching={chartDataQuery.isRefetching}
          />
        ) : null}
        {chartData ? (
          <AddEditDataModalContent
            inputConfig={chartData.addInputConfig.data}
            metricConfigId={chartData.id}
            onSuccessSubmit={() => {
              onSuccessSubmit?.();
              onClose();
            }}
            values={
              value != null
                ? {
                    effectiveDateTime,
                    timeStamp: timestamp,
                    metrics: [
                      {
                        observationId,
                        value: ParseValueForInput(value, label),
                      },
                    ],
                  }
                : undefined
            }
          />
        ) : null}
      </AddEditDataModalContentContainer>
    </Modal.Root>
  );
}
